import React, { useState, useEffect, useRef } from 'react';
import { User, Users, Shield, Upload, Save, Trash2, Camera, FileBadge, Building, Briefcase, Plus, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile State
  const [profile, setProfile] = useState({ id: 1, nome: '', email: '', foto_url: '' });
  const [newPhoto, setNewPhoto] = useState(null);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
  const fileInputRef = useRef(null);

  // Users State
  const [usersList, setUsersList] = useState([]);
  const [newUser, setNewUser] = useState({ nome: '', email: '' });

  // Certificate State
  const [certName, setCertName] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [certPassword, setCertPassword] = useState('');

  // Fiscal & Company State
  const [fiscalData, setFiscalData] = useState({
    cnpj: '', razao_social: '', nome_fantasia: '', telefone: '', email: '', ie: '', im: '', cnae: '', crt: 1,
    logradouro: '', numero: '', complemento: '', bairro: '', cMun: '', xMun: '', uf: 'SP', cep: '',
    ambiente_nfe: 2, serie_nfe: 1, proxima_nfe: 1,
    lista_ncm: [
      { codigo: '94033000', descricao: 'Móveis de madeira para escritórios' },
      { codigo: '95059000', descricao: 'Artigos para festas, carnaval ou outros divertimentos' },
      { codigo: '39269090', descricao: 'Outras obras de plástico (Adereços)' },
      { codigo: '65050090', descricao: 'Chapéus e outros artefatos de uso semelhante' },
      { codigo: '67029000', descricao: 'Flores, folhagens e frutos artificiais' }
    ],
    padrao_ncm: '94033000',
    lista_cfop: [
      { codigo: '5101', descricao: '5101 - Venda de produção do estabelecimento (Estadual)' },
      { codigo: '5102', descricao: '5102 - Venda de mercadoria adquirida de terceiros (Estadual)' },
      { codigo: '5405', descricao: '5405 - Venda de mercadoria c/ ST (Estadual)' },
      { codigo: '5949', descricao: '5949 - Outra saída de mercadoria (Estadual)' },
      { codigo: '6101', descricao: '6101 - Venda de produção fora do estado (Interestadual)' },
      { codigo: '6102', descricao: '6102 - Venda de mercadoria adquirida de terceiros (Interestadual)' },
      { codigo: '6405', descricao: '6405 - Venda de mercadoria c/ ST fora do estado (Interestadual)' },
      { codigo: '6949', descricao: '6949 - Outra saída fora do estado (Interestadual)' }
    ],
    padrao_cfop: '5102',
    lista_situacao_tributaria: [
      { codigo: '101', descricao: '101 - Tributada pelo Simples Nacional com permissão de crédito' },
      { codigo: '102', descricao: '102 - Tributada pelo Simples Nacional sem permissão de crédito' },
      { codigo: '103', descricao: '103 - Isenção do ICMS no Simples Nacional para faixa de receita bruta' },
      { codigo: '201', descricao: '201 - Tributada pelo Simples com permissão de crédito e cobrança ST' },
      { codigo: '202', descricao: '202 - Tributada pelo Simples sem permissão de crédito e cobrança ST' },
      { codigo: '300', descricao: '300 - Imune (Simples Nacional)' },
      { codigo: '400', descricao: '400 - Não tributada pelo Simples Nacional' },
      { codigo: '500', descricao: '500 - ICMS cobrado anteriormente por substituição tributária' },
      { codigo: '900', descricao: '900 - Outros (Simples Nacional)' },
      { codigo: '00', descricao: 'CST 00 - Tributada integralmente (Regime Normal)' },
      { codigo: '20', descricao: 'CST 20 - Com redução de base de cálculo (Regime Normal)' },
      { codigo: '40', descricao: 'CST 40 - Isenta (Regime Normal)' },
      { codigo: '41', descricao: 'CST 41 - Não tributada (Regime Normal)' },
      { codigo: '60', descricao: 'CST 60 - ICMS cobrado anteriormente por ST (Regime Normal)' }
    ],
    padrao_situacao_tributaria: '102'
  });

  const [newNcm, setNewNcm] = useState({ codigo: '', descricao: '' });
  const [newCfop, setNewCfop] = useState({ codigo: '', descricao: '' });
  const [newSitTrib, setNewSitTrib] = useState({ codigo: '', descricao: '' });

  useEffect(() => {
    fetchProfile();
    fetchUsers();
    fetchConfig();
  }, []);

  const fetchProfile = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios.php?id=${userId}`);
      const data = await response.json();
      if (data && data.length > 0) {
        setProfile(data[0]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios.php`);
      const data = await response.json();
      setUsersList(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/configuracoes.php`);
      const data = await response.json();
      if (data) {
        if (data.certificado_nome) setCertName(data.certificado_nome);
        setFiscalData(prev => ({
          ...prev,
          cnpj: data.cnpj || '', razao_social: data.razao_social || '', nome_fantasia: data.nome_fantasia || '',
          telefone: data.telefone || '', email: data.email || '',
          ie: data.ie || '', im: data.im || '', cnae: data.cnae || '', crt: data.crt || 1,
          logradouro: data.logradouro || '', numero: data.numero || '', complemento: data.complemento || '',
          bairro: data.bairro || '', cMun: data.cMun || '', xMun: data.xMun || '', uf: data.uf || 'SP', cep: data.cep || '',
          ambiente_nfe: (data.ambiente_nfe !== undefined && data.ambiente_nfe !== null) ? Number(data.ambiente_nfe) : 2, 
          serie_nfe: Number(data.serie_nfe) || 1, 
          proxima_nfe: Number(data.proxima_nfe) || 1,
          lista_ncm: Array.isArray(data.lista_ncm) && data.lista_ncm.length > 0 ? data.lista_ncm : prev.lista_ncm,
          padrao_ncm: data.padrao_ncm || prev.padrao_ncm,
          lista_cfop: Array.isArray(data.lista_cfop) && data.lista_cfop.length > 0 ? data.lista_cfop : prev.lista_cfop,
          padrao_cfop: data.padrao_cfop || prev.padrao_cfop,
          lista_situacao_tributaria: Array.isArray(data.lista_situacao_tributaria) && data.lista_situacao_tributaria.length > 0 ? data.lista_situacao_tributaria : prev.lista_situacao_tributaria,
          padrao_situacao_tributaria: data.padrao_situacao_tributaria || prev.padrao_situacao_tributaria
        }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddFiscalOption = (listKey, defaultKey, newItemState, setNewItemState) => {
    const codigo = (newItemState.codigo || '').trim();
    const descricao = (newItemState.descricao || '').trim();
    if (!codigo) {
      showAlert('Aviso', 'Informe o código para adicionar à lista.');
      return;
    }
    setFiscalData(prev => {
      const currentList = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      if (currentList.some(item => String(item.codigo) === String(codigo))) {
        showAlert('Aviso', 'Este código já está cadastrado na lista.');
        return prev;
      }
      const updatedList = [...currentList, { codigo, descricao: descricao || codigo }];
      return {
        ...prev,
        [listKey]: updatedList,
        [defaultKey]: prev[defaultKey] || codigo
      };
    });
    setNewItemState({ codigo: '', descricao: '' });
  };

  const handleRemoveFiscalOption = (listKey, defaultKey, codigoToRemove) => {
    setFiscalData(prev => {
      const currentList = Array.isArray(prev[listKey]) ? prev[listKey] : [];
      if (currentList.length <= 1) {
        showAlert('Aviso', 'Mantenha ao menos uma opção na lista.');
        return prev;
      }
      const updatedList = currentList.filter(item => String(item.codigo) !== String(codigoToRemove));
      const newDefault = String(prev[defaultKey]) === String(codigoToRemove)
        ? (updatedList[0] ? updatedList[0].codigo : '')
        : prev[defaultKey];
      return {
        ...prev,
        [listKey]: updatedList,
        [defaultKey]: newDefault
      };
    });
  };

  // -- Profile Handlers --
  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    
    const formData = new FormData();
    formData.append('id', userId);
    formData.append('nome', profile.nome);
    formData.append('email', profile.email);
    if (newPhoto) {
      formData.append('foto', newPhoto);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?action=update`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Aviso','Perfil atualizado com sucesso!');
        fetchProfile();
        // Disparar evento para atualizar o Topbar
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        showAlert('Aviso','Erro ao atualizar: ' + data.error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showAlert('Aviso', 'Preencha todos os campos de senha.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?action=change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: userId,
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Sucesso', 'Sua senha foi redefinida!');
        setPasswordData({ currentPassword: '', newPassword: '' });
      } else {
        showAlert('Erro', data.error);
      }
    } catch (e) {
      console.error(e);
      showAlert('Erro', 'Falha na comunicação com o servidor.');
    }
  };

  // -- Users Handlers --
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.nome || !newUser.email) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (data.success) {
        setNewUser({ nome: '', email: '' });
        fetchUsers();
      } else {
        showAlert('Aviso','Erro ao criar usuário');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteUser = async (id) => {
    if (id === 1) {
      showAlert('Aviso','Não é possível apagar o administrador principal!');
      return;
    }
    const confirmed = await showConfirm('Confirmação', 'Apagar este usuário?');
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        showAlert('Aviso',data.error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // -- Cert Handlers --
  const handleCertUpload = async (e) => {
    e.preventDefault();
    if (!certFile) return;

    const formData = new FormData();
    formData.append('certificado', certFile);
    formData.append('senha_certificado', certPassword);

    try {
      const res = await fetch(`${API_BASE_URL}/configuracoes.php?action=upload_cert`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Sucesso', 'Certificado validado, criptografado e salvo com sucesso!');
        setCertName(data.certificado_nome);
        setCertFile(null);
        setCertPassword('');
      } else {
        showAlert('Erro ao Enviar Certificado', data.error);
      }
    } catch (e) {
      console.error(e);
      showAlert('Erro', 'Falha na comunicação com o servidor.');
    }
  };

  // -- Fiscal Handlers --
  const handleFiscalChange = (e) => {
    const { name, value } = e.target;
    const val = (name === 'ambiente_nfe' || name === 'crt' || name === 'serie_nfe' || name === 'proxima_nfe') ? Number(value) : value;
    setFiscalData(prev => ({ ...prev, [name]: val }));
  };

  const handleSaveFiscal = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/configuracoes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fiscalData)
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Aviso', 'Dados Fiscais salvos com sucesso!');
        fetchConfig();
      } else {
        showAlert('Aviso', 'Erro ao salvar: ' + data.error);
      }
    } catch (e) {
      console.error(e);
      showAlert('Aviso', 'Erro de conexão');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="page-subtitle">Gerencie seu perfil, usuários do sistema e segurança</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        
        {/* SIDEBAR TABS */}
        <div className="card" style={{ width: '280px', flexShrink: 0, padding: '1rem', alignSelf: 'flex-start' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'profile' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'profile' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'profile' ? '600' : '400' }}
                onClick={() => setActiveTab('profile')}
              >
                <User size={18} style={{ marginRight: '0.75rem' }} /> Meu Perfil
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'users' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'users' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'users' ? '600' : '400' }}
                onClick={() => setActiveTab('users')}
              >
                <Users size={18} style={{ marginRight: '0.75rem' }} /> Usuários do Sistema
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', padding: '0.75rem 1rem', background: activeTab === 'company' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'company' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'company' ? '600' : '400' }}
                onClick={() => setActiveTab('company')}
              >
                <Briefcase size={18} style={{ marginRight: '0.75rem', flexShrink: 0 }} /> Dados da Empresa
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'cert' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'cert' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'cert' ? '600' : '400' }}
                onClick={() => setActiveTab('cert')}
              >
                <Shield size={18} style={{ marginRight: '0.75rem' }} /> Certificado Digital
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'fiscal' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'fiscal' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'fiscal' ? '600' : '400' }}
                onClick={() => setActiveTab('fiscal')}
              >
                <Building size={18} style={{ marginRight: '0.75rem' }} /> Dados Fiscais (NF-e)
              </button>
            </li>
          </ul>
        </div>

        {/* CONTENT AREA */}
        <div className="card" style={{ flex: '1', minWidth: '400px', padding: '2rem' }}>
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Meu Perfil</h2>
              <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '500px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <div 
                    style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                    onClick={() => fileInputRef.current.click()}
                  >
                    {newPhoto ? (
                      <img src={URL.createObjectURL(newPhoto)} alt="Novo perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : profile.foto_url ? (
                      <img src={profile.foto_url.startsWith('http') ? profile.foto_url : `${API_BASE_URL.replace('/src/php', '')}/${profile.foto_url}`} alt="Perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={32} color="var(--text-muted)" />
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', padding: '2px' }}>
                      <Camera size={14} color="#fff" />
                    </div>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Foto de Perfil</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Clique na imagem para alterar</p>
                    <input 
                      type="file" 
                      accept="image/*" 
                      ref={fileInputRef} 
                      style={{ display: 'none' }} 
                      onChange={(e) => setNewPhoto(e.target.files[0])}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Nome Completo</label>
                  <input type="text" name="nome" value={profile.nome} onChange={handleProfileChange} className="standard-input" required />
                </div>
                <div className="form-group">
                  <label>E-mail (Login)</label>
                  <input type="email" name="email" value={profile.email} onChange={handleProfileChange} className="standard-input" required />
                </div>
                
                <button type="submit" className="btn-success" style={{ alignSelf: 'flex-start' }}>
                  <Save size={18} style={{ marginRight: '0.5rem' }} /> Salvar Alterações
                </button>
              </form>

              <hr style={{ margin: '2rem 0', borderColor: 'var(--border-color)', borderStyle: 'solid', borderWidth: '1px 0 0 0' }} />

              <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Trocar Senha</h3>
              <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '500px' }}>
                <div className="form-group">
                  <label>Senha Atual</label>
                  <input 
                    type="password" 
                    value={passwordData.currentPassword} 
                    onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})} 
                    className="standard-input" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Nova Senha</label>
                  <input 
                    type="password" 
                    value={passwordData.newPassword} 
                    onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})} 
                    className="standard-input" 
                    required 
                  />
                </div>
                
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>
                  <Shield size={18} style={{ marginRight: '0.5rem' }} /> Redefinir Senha
                </button>
              </form>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Usuários do Sistema</h2>
              
              <form onSubmit={handleCreateUser} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--bg-color)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <div className="form-group flex-1">
                  <label>Nome</label>
                  <input type="text" value={newUser.nome} onChange={e => setNewUser({...newUser, nome: e.target.value})} className="standard-input" required placeholder="Nome do novo usuário" />
                </div>
                <div className="form-group flex-1">
                  <label>E-mail</label>
                  <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="standard-input" required placeholder="email@exemplo.com" />
                </div>
                <button type="submit" className="btn-primary" style={{ height: '38px' }}>Adicionar</button>
              </form>

              <div className="table-responsive" style={{ border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Usuário</th>
                      <th>Nível</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>
                          <div className="font-medium">{u.nome}</div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>{u.email}</div>
                        </td>
                        <td><span className="badge badge-success">Admin</span></td>
                        <td>
                          {u.id !== '1' && u.id !== 1 && (
                            <button className="btn-outline" style={{ borderColor: 'transparent', color: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => handleDeleteUser(u.id)} title="Excluir">
                              <Trash2 size={16} /> Remover Acesso
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CERTIFICATE TAB */}
          {activeTab === 'cert' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Certificado Digital</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Faça o upload do seu certificado e-CNPJ ou e-CPF (formato .pfx) para emissão de notas fiscais.</p>

              {certName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '1.5rem', backgroundColor: 'var(--bg-color)' }}>
                  <FileBadge size={32} color="var(--success-color)" />
                  <div>
                    <h4 style={{ margin: 0, color: 'var(--text-main)' }}>
                      Certificado Ativo
                    </h4>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{certName}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleCertUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: '450px' }}>
                <div className="form-group">
                  <label>Selecionar Arquivo (.pfx) <span className="text-danger">*</span></label>
                  <input 
                    type="file" 
                    accept=".pfx"
                    className="standard-input" 
                    onChange={e => setCertFile(e.target.files[0])}
                    style={{ padding: '0.4rem' }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Senha do Certificado Digital <span className="text-danger">*</span></label>
                  <input 
                    type="password" 
                    className="standard-input" 
                    value={certPassword}
                    onChange={e => setCertPassword(e.target.value)}
                    placeholder="Digite a senha do seu certificado A1 (.pfx)"
                    required
                  />
                </div>
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>
                  <Upload size={18} style={{ marginRight: '0.5rem' }} /> Enviar e Validar Certificado
                </button>
              </form>
            </div>
          )}

          {/* FISCAL TAB */}
          {activeTab === 'fiscal' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Dados Fiscais (NF-e)</h2>
              <form onSubmit={handleSaveFiscal} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                
                {/* Identificação */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>1. Identificação da Empresa</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>CNPJ <span className="text-danger">*</span></label>
                      <input type="text" name="cnpj" value={fiscalData.cnpj} onChange={handleFiscalChange} className="standard-input" required placeholder="Apenas números" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Razão Social <span className="text-danger">*</span></label>
                      <input type="text" name="razao_social" value={fiscalData.razao_social} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Nome Fantasia</label>
                      <input type="text" name="nome_fantasia" value={fiscalData.nome_fantasia} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>Inscrição Estadual (IE) <span className="text-danger">*</span></label>
                      <input type="text" name="ie" value={fiscalData.ie} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Inscrição Municipal (IM)</label>
                      <input type="text" name="im" value={fiscalData.im} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>CNAE Fiscal</label>
                      <input type="text" name="cnae" value={fiscalData.cnae} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Regime Tributário (CRT) <span className="text-danger">*</span></label>
                      <select name="crt" value={fiscalData.crt} onChange={handleFiscalChange} className="standard-input" required>
                        <option value={1}>1 - Simples Nacional</option>
                        <option value={2}>2 - Simples Nacional (Excesso de Sublimite)</option>
                        <option value={3}>3 - Regime Normal (Lucro Presumido/Real)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>2. Endereço</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>CEP <span className="text-danger">*</span></label>
                      <input type="text" name="cep" value={fiscalData.cep} onChange={handleFiscalChange} className="standard-input" required placeholder="00000000" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Logradouro (Rua, Av.) <span className="text-danger">*</span></label>
                      <input type="text" name="logradouro" value={fiscalData.logradouro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Número <span className="text-danger">*</span></label>
                      <input type="text" name="numero" value={fiscalData.numero} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <input type="text" name="complemento" value={fiscalData.complemento} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>Bairro <span className="text-danger">*</span></label>
                      <input type="text" name="bairro" value={fiscalData.bairro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Código Município (IBGE) <span className="text-danger">*</span></label>
                      <input type="text" name="cMun" value={fiscalData.cMun} onChange={handleFiscalChange} className="standard-input" required placeholder="Ex: 3550308" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Nome do Município <span className="text-danger">*</span></label>
                      <input type="text" name="xMun" value={fiscalData.xMun} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>UF <span className="text-danger">*</span></label>
                      <input type="text" name="uf" value={fiscalData.uf} onChange={handleFiscalChange} className="standard-input" required maxLength="2" />
                    </div>
                  </div>
                </div>

                {/* Emissão NF-e */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>3. Parâmetros de Emissão</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Ambiente Sefaz <span className="text-danger">*</span></label>
                      <select name="ambiente_nfe" value={fiscalData.ambiente_nfe} onChange={handleFiscalChange} className="standard-input" required>
                        <option value={2}>2 - Homologação (Testes)</option>
                        <option value={1}>1 - Produção (Validade Jurídica)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Série da NF-e <span className="text-danger">*</span></label>
                      <input type="number" name="serie_nfe" value={fiscalData.serie_nfe} onChange={handleFiscalChange} className="standard-input" required min="1" />
                    </div>
                    <div className="form-group">
                      <label>Próxima NF-e <span className="text-danger">*</span></label>
                      <input type="number" name="proxima_nfe" value={fiscalData.proxima_nfe} onChange={handleFiscalChange} className="standard-input" required min="1" />
                    </div>
                  </div>
                </div>

                {/* 4. Listas e Padrões Fiscais dos Produtos (NCM, CFOP e Situação Tributária) */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-main)' }}>
                    4. Opções e Seleção Padrão de Produtos (NCM, CFOP e Situação Tributária)
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                    Cadastre os itens disponíveis nos dropdowns da tela de emissão de Nota Fiscal e escolha qual opção virá selecionada por padrão em cada produto do pedido.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Bloco NCM */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '1.25rem', backgroundColor: 'var(--bg-color)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1rem' }}>Opções de NCM</h4>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Código NCM de 8 dígitos</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-main)', margin: 0 }}>Padrão:</label>
                          <select
                            name="padrao_ncm"
                            value={fiscalData.padrao_ncm}
                            onChange={handleFiscalChange}
                            className="standard-input"
                            style={{ width: 'auto', minWidth: '240px', fontWeight: '600' }}
                          >
                            {(fiscalData.lista_ncm || []).map(item => (
                              <option key={item.codigo} value={item.codigo}>
                                {item.codigo} {item.descricao ? `- ${item.descricao}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          placeholder="Código NCM (ex: 95059000)"
                          maxLength={8}
                          value={newNcm.codigo}
                          onChange={e => setNewNcm({ ...newNcm, codigo: e.target.value.replace(/[^0-9]/g, '') })}
                          className="standard-input"
                          style={{ width: '200px' }}
                        />
                        <input
                          type="text"
                          placeholder="Descrição do NCM"
                          value={newNcm.descricao}
                          onChange={e => setNewNcm({ ...newNcm, descricao: e.target.value })}
                          className="standard-input"
                          style={{ flex: 1, minWidth: '220px' }}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ width: 'auto', padding: '0 1rem' }}
                          onClick={() => handleAddFiscalOption('lista_ncm', 'padrao_ncm', newNcm, setNewNcm)}
                        >
                          <Plus size={16} /> Adicionar NCM
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {(fiscalData.lista_ncm || []).map(item => {
                          const isDefault = String(fiscalData.padrao_ncm) === String(item.codigo);
                          return (
                            <div key={item.codigo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: '#fff', border: isDefault ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', borderRadius: '6px', gap: '0.75rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                                <strong style={{ color: 'var(--text-main)' }}>{item.codigo}</strong>
                                <span style={{ color: 'var(--text-muted)' }}>{item.descricao}</span>
                                {isDefault && (
                                  <span className="badge badge-success" style={{ fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <CheckCircle2 size={11} /> Padrão
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                {!isDefault && (
                                  <button
                                    type="button"
                                    className="btn-outline"
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
                                    onClick={() => setFiscalData(prev => ({ ...prev, padrao_ncm: item.codigo }))}
                                  >
                                    Definir Padrão
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-icon danger"
                                  title="Remover opção"
                                  onClick={() => handleRemoveFiscalOption('lista_ncm', 'padrao_ncm', item.codigo)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bloco CFOP */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '1.25rem', backgroundColor: 'var(--bg-color)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1rem' }}>Opções de CFOP</h4>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Código Fiscal de Operações e Prestações (4 dígitos)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-main)', margin: 0 }}>Padrão:</label>
                          <select
                            name="padrao_cfop"
                            value={fiscalData.padrao_cfop}
                            onChange={handleFiscalChange}
                            className="standard-input"
                            style={{ width: 'auto', minWidth: '240px', fontWeight: '600' }}
                          >
                            {(fiscalData.lista_cfop || []).map(item => (
                              <option key={item.codigo} value={item.codigo}>
                                {item.descricao && item.descricao.startsWith(item.codigo) ? item.descricao : `${item.codigo} - ${item.descricao}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          placeholder="Código CFOP (ex: 5102)"
                          maxLength={4}
                          value={newCfop.codigo}
                          onChange={e => setNewCfop({ ...newCfop, codigo: e.target.value.replace(/[^0-9]/g, '') })}
                          className="standard-input"
                          style={{ width: '200px' }}
                        />
                        <input
                          type="text"
                          placeholder="Descrição do CFOP"
                          value={newCfop.descricao}
                          onChange={e => setNewCfop({ ...newCfop, descricao: e.target.value })}
                          className="standard-input"
                          style={{ flex: 1, minWidth: '220px' }}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ width: 'auto', padding: '0 1rem' }}
                          onClick={() => handleAddFiscalOption('lista_cfop', 'padrao_cfop', newCfop, setNewCfop)}
                        >
                          <Plus size={16} /> Adicionar CFOP
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {(fiscalData.lista_cfop || []).map(item => {
                          const isDefault = String(fiscalData.padrao_cfop) === String(item.codigo);
                          return (
                            <div key={item.codigo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: '#fff', border: isDefault ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', borderRadius: '6px', gap: '0.75rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                                <strong style={{ color: 'var(--text-main)' }}>{item.codigo}</strong>
                                <span style={{ color: 'var(--text-muted)' }}>{item.descricao}</span>
                                {isDefault && (
                                  <span className="badge badge-success" style={{ fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <CheckCircle2 size={11} /> Padrão
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                {!isDefault && (
                                  <button
                                    type="button"
                                    className="btn-outline"
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
                                    onClick={() => setFiscalData(prev => ({ ...prev, padrao_cfop: item.codigo }))}
                                  >
                                    Definir Padrão
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-icon danger"
                                  title="Remover opção"
                                  onClick={() => handleRemoveFiscalOption('lista_cfop', 'padrao_cfop', item.codigo)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bloco Situação Tributária */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '1.25rem', backgroundColor: 'var(--bg-color)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1rem' }}>Opções de Situação Tributária (CSOSN / CST)</h4>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Código de Situação da Operação no Simples Nacional ou CST ICMS</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-main)', margin: 0 }}>Padrão:</label>
                          <select
                            name="padrao_situacao_tributaria"
                            value={fiscalData.padrao_situacao_tributaria}
                            onChange={handleFiscalChange}
                            className="standard-input"
                            style={{ width: 'auto', minWidth: '240px', fontWeight: '600' }}
                          >
                            {(fiscalData.lista_situacao_tributaria || []).map(item => (
                              <option key={item.codigo} value={item.codigo}>
                                {item.descricao && item.descricao.startsWith(item.codigo) ? item.descricao : `${item.codigo} - ${item.descricao}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          placeholder="Código (ex: 102 ou 500)"
                          maxLength={4}
                          value={newSitTrib.codigo}
                          onChange={e => setNewSitTrib({ ...newSitTrib, codigo: e.target.value.replace(/[^0-9]/g, '') })}
                          className="standard-input"
                          style={{ width: '200px' }}
                        />
                        <input
                          type="text"
                          placeholder="Descrição da Situação Tributária"
                          value={newSitTrib.descricao}
                          onChange={e => setNewSitTrib({ ...newSitTrib, descricao: e.target.value })}
                          className="standard-input"
                          style={{ flex: 1, minWidth: '220px' }}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ width: 'auto', padding: '0 1rem' }}
                          onClick={() => handleAddFiscalOption('lista_situacao_tributaria', 'padrao_situacao_tributaria', newSitTrib, setNewSitTrib)}
                        >
                          <Plus size={16} /> Adicionar Situação
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {(fiscalData.lista_situacao_tributaria || []).map(item => {
                          const isDefault = String(fiscalData.padrao_situacao_tributaria) === String(item.codigo);
                          return (
                            <div key={item.codigo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: '#fff', border: isDefault ? '1px solid var(--primary-color)' : '1px solid var(--border-color)', borderRadius: '6px', gap: '0.75rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
                                <strong style={{ color: 'var(--text-main)' }}>{item.codigo}</strong>
                                <span style={{ color: 'var(--text-muted)' }}>{item.descricao}</span>
                                {isDefault && (
                                  <span className="badge badge-success" style={{ fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <CheckCircle2 size={11} /> Padrão
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                {!isDefault && (
                                  <button
                                    type="button"
                                    className="btn-outline"
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
                                    onClick={() => setFiscalData(prev => ({ ...prev, padrao_situacao_tributaria: item.codigo }))}
                                  >
                                    Definir Padrão
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-icon danger"
                                  title="Remover opção"
                                  onClick={() => handleRemoveFiscalOption('lista_situacao_tributaria', 'padrao_situacao_tributaria', item.codigo)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn-success" style={{ alignSelf: 'flex-start', marginTop: '1rem', padding: '0.75rem 2rem' }}>
                  <Save size={18} style={{ marginRight: '0.5rem' }} /> Salvar Dados Fiscais
                </button>
              </form>
            </div>
          )}

          {/* COMPANY / BUDGET DATA TAB */}
          {activeTab === 'company' && (
            <div>
              <h2 style={{ marginBottom: '0.5rem', color: 'var(--text-main)' }}>Dados da Empresa</h2>
              <p style={{ marginBottom: '1.5rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Estes dados (Razão Social, Nome Fantasia, CNPJ, Endereço, Telefone e E-mail) são utilizados para compor o cabeçalho dos Orçamentos e documentos emitidos para seus clientes.
              </p>

              <form onSubmit={handleSaveFiscal} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Identificação */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>1. Informações da Empresa</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Razão Social <span className="text-danger">*</span></label>
                      <input type="text" name="razao_social" value={fiscalData.razao_social} onChange={handleFiscalChange} className="standard-input" required placeholder="Ex: Alexandre Folia Comércio Ltda" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Nome Fantasia</label>
                      <input type="text" name="nome_fantasia" value={fiscalData.nome_fantasia} onChange={handleFiscalChange} className="standard-input" placeholder="Ex: Alefolia" />
                    </div>
                    <div className="form-group">
                      <label>CNPJ <span className="text-danger">*</span></label>
                      <input type="text" name="cnpj" value={fiscalData.cnpj} onChange={handleFiscalChange} className="standard-input" required placeholder="00.000.000/0001-00" />
                    </div>
                    <div className="form-group">
                      <label>Telefone / WhatsApp</label>
                      <input type="text" name="telefone" value={fiscalData.telefone} onChange={handleFiscalChange} className="standard-input" placeholder="(11) 99999-9999" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>E-mail Comercial / Contato</label>
                      <input type="email" name="email" value={fiscalData.email} onChange={handleFiscalChange} className="standard-input" placeholder="contato@empresa.com.br" />
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>2. Endereço da Empresa</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>CEP <span className="text-danger">*</span></label>
                      <input type="text" name="cep" value={fiscalData.cep} onChange={handleFiscalChange} className="standard-input" required placeholder="00000-000" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Logradouro (Rua, Av.) <span className="text-danger">*</span></label>
                      <input type="text" name="logradouro" value={fiscalData.logradouro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Número <span className="text-danger">*</span></label>
                      <input type="text" name="numero" value={fiscalData.numero} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <input type="text" name="complemento" value={fiscalData.complemento} onChange={handleFiscalChange} className="standard-input" placeholder="Sala, Bloco, etc." />
                    </div>
                    <div className="form-group">
                      <label>Bairro <span className="text-danger">*</span></label>
                      <input type="text" name="bairro" value={fiscalData.bairro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Cidade / Município <span className="text-danger">*</span></label>
                      <input type="text" name="xMun" value={fiscalData.xMun} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>UF <span className="text-danger">*</span></label>
                      <input type="text" name="uf" value={fiscalData.uf} onChange={handleFiscalChange} className="standard-input" required maxLength="2" placeholder="SP" />
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn-success" style={{ alignSelf: 'flex-start', padding: '0.75rem 2rem' }}>
                  <Save size={18} style={{ marginRight: '0.5rem' }} /> Salvar Dados da Empresa
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
